import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { Sparkles, X, Send, ArrowRight, MinusCircle } from "lucide-react";

const KEY = "atom.askdock.minimized";

/**
 * Floating "Ask Atom" chat dock — visible on every page (mounted by AppShell).
 * Toggle FAB → opens a 380x520 panel anchored bottom-right.
 */
export function AskAtomDock() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(localStorage.getItem(KEY) === "1");
  const [messages, setMessages] = useState([
    { from: "atom", text: "Hi — I'm Atom Nucleus v3.5. Ask me anything about your workspace.", model: "Atom Nucleus v3.5" },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setMessages(m => [...m, { from: "user", text }]);
    setSending(true);
    try {
      const r = await api.post("/atom/chat", { message: text, context_page: location.pathname }, { timeout: 60000 });
      setMessages(m => [...m, { from: "atom", text: r.data.text, model: r.data.model, actions: r.data.actions }]);
    } catch (e) {
      setMessages(m => [...m, { from: "atom", text: "I'm offline right now. Try again in a moment.", model: "Atom Nucleus v3.5" }]);
    } finally { setSending(false); }
  };

  const suggestions = [
    "How many P1 alerts are open?",
    "What's happening in Lagos?",
    "Atom Proton model health?",
    "How many STRs have we filed?",
  ];

  if (minimized) {
    return (
      <button
        data-testid="askatom-restore"
        onClick={() => { setMinimized(false); localStorage.removeItem(KEY); }}
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-1.5 px-3 py-1.5 bg-ink text-white text-[11px] rounded-full shadow-lg hover:bg-navy"
      >
        <Sparkles size={11} strokeWidth={1.5} className="text-amber" /> Atom
      </button>
    );
  }

  return (
    <>
      {/* FAB */}
      {!open && (
        <button
          data-testid="askatom-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 group inline-flex items-center gap-2 px-4 py-3 bg-ink text-white rounded-full shadow-[0_8px_24px_rgba(11,43,92,0.25)] hover:bg-navy hover:shadow-[0_12px_32px_rgba(11,43,92,0.35)] transition-all"
        >
          <div className="w-5 h-5 rounded bg-amber flex items-center justify-center">
            <div className="grid grid-cols-3 gap-[1px] w-2.5 h-2.5">
              {[1,1,1, 1,0,1, 1,1,1].map((v,i) => <span key={i} className={`w-[2.5px] h-[2.5px] ${v ? 'bg-ink' : 'bg-transparent'}`} />)}
            </div>
          </div>
          <span className="text-[12px] font-medium tracking-tight">Ask Atom</span>
          <Sparkles size={11} strokeWidth={1.5} className="text-amber opacity-70 group-hover:opacity-100" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div data-testid="askatom-panel" className="fixed bottom-5 right-5 z-40 w-[380px] h-[540px] bg-white border border-line rounded-2xl shadow-[0_4px_12px_rgba(11,43,92,0.08),0_24px_48px_rgba(11,43,92,0.22)] flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-line bg-gradient-to-r from-amber/10 to-white">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-amber flex items-center justify-center">
                <Sparkles size={13} className="text-white" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-[13px] font-medium tracking-tight">Ask Atom</div>
                <div className="text-[10px] text-ink-subtle">Atom Nucleus v3.5 · always-on</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => { setMinimized(true); localStorage.setItem(KEY, "1"); setOpen(false); }} className="p-1 hover:bg-bg-hover rounded" title="Minimise"><MinusCircle size={13} strokeWidth={1.5} /></button>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-bg-hover rounded" data-testid="askatom-close"><X size={14} strokeWidth={1.5} /></button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-[12.5px]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 ${m.from === "user" ? "bg-ink text-white" : "bg-bg-warm text-ink border border-line/60"}`}>
                  <div className="whitespace-pre-wrap" style={{ wordBreak: "break-word" }} dangerouslySetInnerHTML={{ __html: m.text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} />
                  {m.actions && m.actions.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {m.actions.map((a, j) => (
                        <Link key={j} to={a.to} onClick={() => setOpen(false)} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-white border border-line rounded-full hover:bg-bg-hover">
                          {a.label} <ArrowRight size={9} strokeWidth={1.5} />
                        </Link>
                      ))}
                    </div>
                  )}
                  {m.from === "atom" && m.model && (
                    <div className="text-[9px] text-ink-subtle mt-1.5 opacity-70">{m.model}</div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-bg-warm border border-line/60 rounded-2xl px-3 py-2 flex gap-1 items-center">
                  <span className="w-1 h-1 rounded-full bg-ink animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1 h-1 rounded-full bg-ink animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1 h-1 rounded-full bg-ink animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {suggestions.map(s => (
                <button key={s} onClick={() => send(s)} className="text-[10.5px] px-2 py-1 bg-bg-warm border border-line rounded-full hover:bg-bg-hover">
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="border-t border-line p-3 flex items-center gap-2">
            <input
              data-testid="askatom-input"
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && send()}
              placeholder="Ask anything…"
              className="flex-1 bg-bg-warm border border-line px-3 py-2 text-[12px] rounded-full focus:outline-none focus:ring-2 focus:ring-amber/30 focus:border-amber"
            />
            <button
              data-testid="askatom-send"
              onClick={() => send()}
              disabled={!input.trim() || sending}
              className="w-9 h-9 bg-ink text-white rounded-full hover:bg-navy flex items-center justify-center disabled:opacity-40"
            >
              <Send size={13} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
